import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import DOMPurify from 'dompurify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { safeJsonParse } from '@rcw/utils';
import { useAuth } from 'context/AuthContext';
import { useTranslation } from 'i18n/LocaleContext';
import MetaDecorator from 'components/layout/MetaDecorator';

const GATEWAY_BASE = process.env.REACT_APP_CUSTOMERS;
const RCP_API_BASE = process.env.REACT_APP_AUTHORITY;
const HARDWARE_PRODUCTS_KEY = 'roland-cloud/key/json/HardwareProductsList.json';

const TAB_KEY = 'key';
const TAB_SERIAL = 'serial_number';

function InfoIcon() {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width='26' height='26' fill='none'>
      <g
        stroke='#39a9f7'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
      >
        <path d='M13 24.636c6.426 0 11.636-5.21 11.636-11.636S19.426 1.364 13 1.364 1.364 6.574 1.364 13 6.574 24.636 13 24.636M13 8.346V13M13 17.654h.011' />
      </g>
      <defs>
        <clipPath id='a'>
          <path fill='#fff' d='M0 0h26v26H0z' />
        </clipPath>
      </defs>
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'><path stroke='#39a9f7' strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='m20.932 2-2 2m0 0 3 3-3.5 3.5-3-3m3.5-3.5-3.5 3.5m-4.11 4.11a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0 4.11-4.11' /></svg>
  );
}

function MusicNoteIcon() {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'>
      <path stroke='#39a9f7' strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M9 18V5l12-2v13' />
      <path stroke='#39a9f7' strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6M18 19a3 3 0 1 0 0-6 3 3 0 0 0 0 6' />
    </svg>
  );
}

const getIdToken = () => {
  const key = `oidc.user:${process.env.REACT_APP_AUTHORITY}:${process.env.REACT_APP_CLIENT_ID}`;
  const stored = localStorage.getItem(key);
  return safeJsonParse(stored)?.id_token ?? null;
};

export function KeyPage() {
  const { user, isLoading: authLoading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(TAB_KEY);

  // Key tab state
  const [entitlementKey, setEntitlementKey] = useState('');
  const [validKey, setValidKey] = useState(false);

  // Serial number tab state
  const [serialNumber, setSerialNumber] = useState('');
  const [hardwareProduct, setHardwareProduct] = useState('');
  const [hardwareProducts, setHardwareProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Shared state
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);
  const [hasSubmittedSerial, setHasSubmittedSerial] = useState(false);

  const keyInputRef = useRef(null);

  // Pre-fill key from ?key= query param
  useEffect(() => {
    const queryKey = searchParams.get('key');
    if (queryKey) {
      setEntitlementKey(decodeURIComponent(queryKey));
    }
  }, [searchParams]);

  // Validate key input
  useEffect(() => {
    setValidKey(entitlementKey.trim().length > 0);
  }, [entitlementKey]);

  // Load hardware products once authenticated
  useEffect(() => {
    if (authLoading || !user || user.guest) return;
    setLoadingProducts(true);
    fetch(`${RCP_API_BASE}json/static?key=${HARDWARE_PRODUCTS_KEY}`)
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : (data?.data || []);
        const now = new Date();
        const filtered = list
          .filter((p) => {
            const { publishFrom, publishTo } = p;
            if (!publishFrom && !publishTo) return true;
            if (publishFrom && !publishTo) return now >= new Date(publishFrom);
            if (!publishFrom && publishTo) return now < new Date(publishTo);
            return now >= new Date(publishFrom) && now < new Date(publishTo);
          })
          .map((p) => ({ ...p, displayName: p.displayName || p.code }))
          .sort((a, b) => (a.displayName > b.displayName ? 1 : -1));
        setHardwareProducts(filtered);
      })
      .catch((err) => console.error('[KeyPage] Failed to load hardware products:', err))
      .finally(() => setLoadingProducts(false));
  }, [user, authLoading]);

  const clearAlert = () => setAlert(null);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    clearAlert();
    setHasSubmittedSerial(false);
  };

  const getAuthHeaders = () => {
    const idToken = getIdToken();
    return {
      'authorization': `Bearer ${idToken}`,
      'content-type': 'application/json',
    };
  };

  const handleRegisterResult = (res) => {
    const isNonRenewing = res?.data?.some
      ? res.data.some((d) => d?.offerTierPlanId === 25 || d?.offerTierPlanId === -1)
      : false;

    if (isNonRenewing) {
      setAlert({ type: 'info', message: t('RVS.COMPONENT.ENTITLEMENT_KEY.HTML.RCC_MEMBERSHIP_MESSAGE', 'Your Roland Cloud membership has been activated.'), isHtml: true });
    } else {
      setAlert({ type: 'info', message: t('RVS.COMPONENT.ENTITLEMENT_KEY.HTML.ALERT_REGISTRATION_SUCCESSFUL', 'Registration successful!') });
      setTimeout(() => navigate('/thank-you-key'), 1500);
    }
  };

  const handleError = (err) => {
    const status = err?.status || err?.response?.status;
    if (status === 400) {
      setAlert({ type: 'error', message: t('RVS.COMPONENT.ENTITLEMENT_KEY.HTML.ALERT_UNABLE_TO_REGISTER', 'Unable to register. Please check your key and try again.'), isHtml: true });
    } else if (status === 404) {
      setAlert({ type: 'error', message: t('RVS.COMPONENT.ENTITLEMENT_KEY.HTML.ALERT_NOT_FOUND', 'Key not found. Please verify the key and try again.'), isHtml: true });
    } else if (status === 409) {
      setAlert({ type: 'info', message: t('RVS.COMPONENT.ENTITLEMENT_KEY.HTML.ALERT_ALREADY_REGISTERED', 'This key has already been registered.'), isHtml: true });
    } else {
      setAlert({ type: 'error', message: 'An error occurred. Please try again later.' });
    }
  };

  const handleSubmitKey = async (e) => {
    e.preventDefault();
    if (!validKey || submitting) return;
    clearAlert();
    setSubmitting(true);
    try {
      const res = await fetch(
        `${GATEWAY_BASE}/notifications/key/${encodeURIComponent(entitlementKey.trim())}/register`,
        { method: 'POST', headers: getAuthHeaders() },
      );
      if (!res.ok) {
        handleError({ status: res.status });
        return;
      }
      const data = await res.json();
      handleRegisterResult(data);
    } catch (err) {
      handleError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitSerial = async (e) => {
    e.preventDefault();
    setHasSubmittedSerial(true);
    if (!serialNumber.trim() || !hardwareProduct || submitting) return;
    clearAlert();
    setSubmitting(true);
    try {
      const url = `${GATEWAY_BASE}/notifications/key/${encodeURIComponent(serialNumber.trim())}/register?productCode=${encodeURIComponent(hardwareProduct)}`;
      const res = await fetch(url, { method: 'POST', headers: getAuthHeaders() });
      if (!res.ok) {
        handleError({ status: res.status });
        return;
      }
      const data = await res.json();
      handleRegisterResult(data);
    } catch (err) {
      handleError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const isGuest = !user || user.guest;

  const metaDecorator = (
    <MetaDecorator
      title={t('RVS.COMPONENT.ENTITLEMENT_KEY.TITLE', 'Roland Cloud Activation')}
      description='Register your Roland Cloud key or hardware serial number.'
      link='key'
      addPostfixTitle
      noIndex
    />
  );

  if (authLoading) {
    return (
      <div className='flex-1 max-w-[860px] w-full mx-auto py-10 px-6'>
        {metaDecorator}
        <div className='text-center py-16 text-gray-500'>{t('COMMON.LOADING', 'Loading...')}</div>
      </div>
    );
  }

  const disabledKey = isGuest || !validKey || submitting;
  const disabledSerial = isGuest || !serialNumber.trim() || submitting;

  return (
    <div className='flex-1 max-w-[860px] w-full mx-auto py-10 px-6'>
      {metaDecorator}
      <h1 className='text-5xl font-bold text-center py-10'>{t('RVS.COMPONENT.ENTITLEMENT_KEY.TITLE', 'Roland Cloud Activation')}</h1>

      {isGuest && (
      <div className='rounded-[25px] border-black/30 border flex flex-col md:flex-row md:items-center justify-between gap-6 p-[35px] mb-10'>
        <div className='flex items-center flex-grow'>
          <span className='flex-shrink-0' style={{ marginRight: '25px' }}>
            <InfoIcon />
          </span>
          <p style={{ fontSize: '16px' }}>
            {t('RVS.COMPONENT.ENTITLEMENT_KEY.PLEASE_SIGN_IN', 'You must have a Roland Account before you can enter your Key to register. Please sign in or create an account to continue.')}
          </p>
        </div>
        <button
          type='button'
          className='btn-primary whitespace-nowrap flex-shrink-0 !py-[14px] !px-7 !text-[18px]'
          onClick={() => navigate('/login', { state: { returnUrl: '/cloud/key' } })}
        >
          {t('RVS.COMPONENT.ENTITLEMENT_KEY.BUTTON.SIGNIN', 'Sign In Here')}
        </button>
      </div>
      )}

      <div className='flex justify-center mb-10 gap-4'>
        <button
          type='button'
          className={`btn-base flex-1 !py-2 md:!py-[14px] !px-4 md:!px-7 !text-base md:!text-[22px] !font-medium !border md:!h-[52px] ${activeTab === TAB_KEY ? 'bg-roland-blue text-white border-roland-blue' : 'border-black/30 !text-black/50'} `}
          onClick={() => handleTabChange(TAB_KEY)}
        >
          {t('RVS.COMPONENT.ENTITLEMENT_KEY.TAB_01', 'KEY')}
        </button>
        <button
          type='button'
          className={`btn-base flex-1 !py-2 md:!py-[14px] !px-4 md:!px-7 !text-base md:!text-[22px] !font-medium !border md:!h-[52px] ${activeTab === TAB_SERIAL ? 'bg-roland-blue text-white border-roland-blue' : 'border-black/30 !text-black/50'} `}
          onClick={() => handleTabChange(TAB_SERIAL)}
        >
          {t('RVS.COMPONENT.ENTITLEMENT_KEY.TAB_02', 'SERIAL NUMBER')}
        </button>
      </div>

      {alert && (
      <div className={`p-4 rounded-xl mb-6 text-sm border ${alert.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
        {alert.isHtml
        // eslint-disable-next-line react/no-danger
          ? <span className='alert-message' dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(alert.message) }} />
          : alert.message}
      </div>
      )}

      {activeTab === TAB_KEY && (
      <form onSubmit={handleSubmitKey}>
        <label htmlFor='entitlement-key' className='block mb-4' style={{ paddingLeft: '20px', fontSize: '18px' }}>
          {t('RVS.COMPONENT.ENTITLEMENT_KEY.HTML.DESCRIPTION_KEY', 'Please enter your Key to register')}
        </label>
        <div className='flex flex-col md:flex-row items-stretch md:items-center gap-3 mb-6'>
          <div className='flex-1 relative'>
            <span className='absolute left-4 top-1/2 -translate-y-1/2' style={{ margin: '0px 15px' }}>
              <KeyIcon />
            </span>
            <input
              id='entitlement-key'
              ref={keyInputRef}
              type='text'
              value={entitlementKey}
              onChange={(e) => setEntitlementKey(e.target.value)}
              className='w-full rounded-full bg-transparent border border-black/20 text-black focus:border-[#39a9f7] focus:ring-1 focus:ring-[#39a9f7] outline-none transition-all disabled:opacity-50 h-[52px]'
              style={{ fontSize: '16px', padding: '0px 67px' }}
              placeholder={t('RVS.COMPONENT.ENTITLEMENT_KEY.LABEL.KEY', 'Enter your Key here')}
              autoComplete='off'
              maxLength={100}
              disabled={isGuest}
            />
          </div>
          <button
            type='submit'
            className='btn-primary !text-[18px] md:min-w-[140px] h-[52px] flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed'
            disabled={disabledKey}
          >
            {submitting ? '...' : t('RVS.COMPONENT.ENTITLEMENT_KEY.BUTTON.SUBMIT', 'SUBMIT')}
          </button>
        </div>
      </form>
      )}

      {activeTab === TAB_SERIAL && (
      <form onSubmit={handleSubmitSerial}>
        <label htmlFor='hardware-product' className='block mb-4' style={{ paddingLeft: '10px', fontSize: '18px' }}>
          {t('RVS.COMPONENT.ENTITLEMENT_KEY.HTML.DESCRIPTION_SERIAL_NUMBER', 'Please select your product and enter your Serial Number to register')}
        </label>
        <div className='flex flex-col gap-1 mb-6'>
          <div className='flex flex-col md:flex-row items-stretch md:items-center gap-3'>
            <div className='flex-1 relative'>
              <span className='absolute left-4 top-1/2 -translate-y-1/2' style={{ margin: '0px 15px' }}>
                <MusicNoteIcon />
              </span>
              <select
                id='hardware-product'
                value={hardwareProduct}
                onChange={(e) => { setHardwareProduct(e.target.value); }}
                className={`w-full bg-white border rounded-full focus:ring-1 outline-none cursor-pointer transition-all disabled:opacity-50 h-[52px] appearance-none ${!hardwareProduct ? 'text-gray-400' : 'text-black'} ${hasSubmittedSerial && !hardwareProduct ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-black/20 focus:border-[#39a9f7] focus:ring-[#39a9f7]'}`}
                style={{ fontSize: '16px', padding: '0px 67px' }}
                disabled={loadingProducts || isGuest}
              >
                <option value='' className='text-gray-400'>
                  {loadingProducts ? t('COMMON.LOADING', 'Loading...') : t('RVS.COMPONENT.ENTITLEMENT_KEY.LABEL.PRODUCT', 'Select a product')}
                </option>
                {hardwareProducts.map((p) => (
                  <option key={p.code} value={p.code} className='text-black'>
                    {p.displayName}
                  </option>
                ))}
              </select>
              <span className='absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-black/50'>
                ▼
              </span>
            </div>
            <div className='flex-1 relative'>
              <span className='absolute left-4 top-1/2 -translate-y-1/2' style={{ margin: '0px 15px' }}>
                <KeyIcon />
              </span>
              <input
                id='serial-number'
                type='text'
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                className='w-full bg-transparent border border-black/20 rounded-full text-black focus:border-[#39a9f7] focus:ring-1 focus:ring-[#39a9f7] outline-none transition-all disabled:opacity-50 h-[52px]'
                style={{ fontSize: '16px', padding: '0px 67px' }}
                placeholder={t('RVS.COMPONENT.ENTITLEMENT_KEY.LABEL.SERIAL_NUMBER', 'Enter your Serial Number here')}
                autoComplete='off'
                maxLength={30}
                disabled={isGuest}
              />
            </div>
            <button
              type='submit'
              className='btn-primary !text-[18px] md:min-w-[140px] h-[52px] flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed'
              disabled={disabledSerial}
            >
              {submitting ? '...' : t('RVS.COMPONENT.ENTITLEMENT_KEY.BUTTON.SUBMIT', 'SUBMIT')}
            </button>
          </div>
          {hasSubmittedSerial && !hardwareProduct && (
          <p style={{ fontSize: '14px', color: '#F73C39' }}>
            {t('RVS.COMPONENT.ENTITLEMENT_KEY.ERROR.PRODUCT_REQUIRED', '*Hardware Product is required')}
          </p>
          )}
        </div>
      </form>
      )}
    </div>
  );
}
